import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";

export default function EventFormDialog({ event, onSaved }) {
  const isEdit = !!event;
  const [open, setOpen] = useState(false);

  const toLocalInput = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  };

  const initial = () => isEdit ? {
    title: event.title || "",
    description: event.description || "",
    type: event.type || "afterwork",
    date: toLocalInput(event.date),
    seats_total: event.seats_total || 20,
    price: event.price ?? 25,
    location: event.location || "",
    image: event.image || "",
  } : {
    title: "", description: "", type: "afterwork", date: "",
    seats_total: 20, price: 25, location: "", image: "",
  };

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleOpenChange = (o) => {
    setOpen(o);
    if (o) setForm(initial());
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("image", data.url);
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur de téléversement");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        seats_total: parseInt(form.seats_total, 10),
        price: parseFloat(form.price),
        date: new Date(form.date).toISOString(),
      };
      if (isEdit) {
        await api.put(`/admin/events/${event.id}`, payload);
        toast.success("Événement mis à jour");
      } else {
        await api.post("/admin/events", payload);
        toast.success("Événement créé");
      }
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const trigger = isEdit ? (
    <Button variant="ghost" size="icon" data-testid={`edit-event-${event.id}`} aria-label="Modifier">
      <Pencil className="w-4 h-4" />
    </Button>
  ) : (
    <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" data-testid="admin-create-event-btn">
      <Plus className="w-4 h-4 mr-2" /> Nouvel événement
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Modifier l'événement" : "Nouvel événement"}</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {isEdit ? "Modifiez les informations de cet événement. Les places réservées sont conservées." : "Créez un afterwork, un atelier cocktail ou un atelier rhum arrangé."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-event-form">
          <div>
            <Label>Titre</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} required className="mt-2 bg-input" data-testid="admin-event-title" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} required className="mt-2 bg-input" data-testid="admin-event-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="mt-2 bg-input" data-testid="admin-event-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="afterwork">Afterwork</SelectItem>
                  <SelectItem value="atelier_cocktail">Atelier Cocktail</SelectItem>
                  <SelectItem value="atelier_rhum">Atelier Rhum</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date & heure</Label>
              <Input type="datetime-local" value={form.date} onChange={(e) => set("date", e.target.value)} required className="mt-2 bg-input" data-testid="admin-event-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Places</Label>
              <Input type="number" min={1} value={form.seats_total} onChange={(e) => set("seats_total", e.target.value)} className="mt-2 bg-input" data-testid="admin-event-seats" />
              {isEdit && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  {event.seats_total - event.seats_available} réservée(s), {event.seats_available} dispo.
                </p>
              )}
            </div>
            <div>
              <Label>Prix (€)</Label>
              <Input type="number" min={0} step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-2 bg-input" data-testid="admin-event-price" />
            </div>
          </div>
          <div>
            <Label>Lieu</Label>
            <Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-2 bg-input" data-testid="admin-event-location" />
          </div>
          <div>
            <Label>Image</Label>
            <div className="mt-2 flex items-center gap-3">
              <label className="flex-1 cursor-pointer">
                <div className="border border-dashed border-border rounded-lg px-4 py-3 text-sm text-muted-foreground bg-input hover:border-primary/50 transition-colors text-center" data-testid="admin-event-image-picker">
                  {uploading ? "Téléversement…" : form.image ? "Changer d'image" : "Choisir une image"}
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={onFileChange} data-testid="admin-event-image-input" />
              </label>
              {form.image && (
                <img src={form.image} alt="Aperçu" className="h-14 w-14 object-cover rounded-lg border border-border" data-testid="admin-event-image-preview" />
              )}
            </div>
            <Input
              value={form.image}
              onChange={(e) => set("image", e.target.value)}
              className="mt-2 bg-input text-xs"
              placeholder="ou URL externe…"
              data-testid="admin-event-image"
            />
          </div>
          <Button type="submit" disabled={saving || uploading} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="admin-event-submit">
            {saving ? (isEdit ? "Enregistrement…" : "Création…") : (isEdit ? "Enregistrer" : "Créer")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
